// Tiered Commission Profile editor (iter-f31) — used for both New and Edit.
//
// Three blocks: staff assignment, treatment selection (collapsed category
// view mirroring Brand Settings → Services) and the tier ladder (base rate
// + up to four ascending thresholds).
import React, { useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChevronDown, ChevronRight, Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";

const MAX_TIERS = 4;

export default function TierProfileDialog({
  draft, onClose, onSaved, staffUsers, takenStaffIds, services, categories, currency,
}) {
  const isEdit = Boolean(draft.staff_id);
  const [staffId, setStaffId] = useState(draft.staff_id || "");
  const [baseRate, setBaseRate] = useState(draft.base_rate || "");
  const [tiers, setTiers] = useState(draft.tiers || []);
  const [picked, setPicked] = useState(() => new Set(draft.service_ids || []));
  // Revenue share % per treatment — 100 for a solo treatment, or the split
  // the admin types for a treatment performed by two people.
  const [shares, setShares] = useState(() => {
    const init = {};
    for (const sid of draft.service_ids || []) {
      const v = (draft.service_shares || {})[sid];
      init[sid] = v === undefined || v === null ? "100" : String(v);
    }
    return init;
  });
  const [open, setOpen] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const fm = (n) => formatBranchMoney(currency, Number(n) || 0);

  const assignable = useMemo(
    () => (staffUsers || [])
      .filter((u) => isEdit ? u.id === staffId : !takenStaffIds.has(u.id))
      .sort((a, b) => (a.full_name || a.email || "").localeCompare(b.full_name || b.email || "")),
    [staffUsers, takenStaffIds, isEdit, staffId],
  );

  const svcByCat = useMemo(() => {
    const q = query.trim().toLowerCase();
    const m = new Map();
    for (const s of services || []) {
      if (s.active === false && !picked.has(s.id)) continue;
      if (q && !(s.name || "").toLowerCase().includes(q)) continue;
      const cid = s.category_id || "__uncategorized__";
      if (!m.has(cid)) m.set(cid, []);
      m.get(cid).push(s);
    }
    for (const list of m.values()) list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return m;
  }, [services, query, picked]);

  const orderedCats = useMemo(() => {
    const cats = (categories || [])
      .filter((c) => (svcByCat.get(c.id) || []).length > 0)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if ((svcByCat.get("__uncategorized__") || []).length > 0) {
      cats.push({ id: "__uncategorized__", name: "Uncategorised" });
    }
    return cats;
  }, [categories, svcByCat]);

  const toggleCat = (cid) => setOpen((prev) => {
    const n = new Set(prev);
    if (n.has(cid)) n.delete(cid); else n.add(cid);
    return n;
  });

  const toggleSvc = (sid) => {
    setPicked((prev) => {
      const n = new Set(prev);
      if (n.has(sid)) n.delete(sid); else n.add(sid);
      return n;
    });
    setShares((prev) => (prev[sid] ? prev : { ...prev, [sid]: "100" }));
  };

  const setAllInCat = (list, on) => {
    setPicked((prev) => {
      const n = new Set(prev);
      for (const s of list) { if (on) n.add(s.id); else n.delete(s.id); }
      return n;
    });
    if (on) {
      setShares((prev) => {
        const n = { ...prev };
        for (const s of list) if (!n[s.id]) n[s.id] = "100";
        return n;
      });
    }
  };

  const setShare = (sid, v) => setShares((prev) => ({ ...prev, [sid]: v }));

  const setTier = (i, patch) => setTiers((prev) => prev.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const addTier = () => setTiers((prev) => prev.length >= MAX_TIERS ? prev : [...prev, { threshold: "", rate: "" }]);
  const removeTier = (i) => setTiers((prev) => prev.filter((_, idx) => idx !== i));

  // Client-side mirror of the server guard so the operator sees the
  // problem before the round-trip.
  const problem = useMemo(() => {
    if (!staffId) return "Select the staff member this profile applies to.";
    const base = parseFloat(baseRate);
    if (!Number.isFinite(base) || base <= 0 || base > 100) return "Enter a base rate between 0 and 100%.";
    if (picked.size === 0) return "Tick at least one treatment.";
    for (const sid of picked) {
      const sh = parseFloat(shares[sid] ?? "100");
      if (!Number.isFinite(sh) || sh <= 0 || sh > 100) {
        const nm = (services || []).find((s) => s.id === sid)?.name || "a treatment";
        return `Revenue share for ${nm} must be between 0 and 100%.`;
      }
    }
    let prevTh = 0;
    let prevRate = base;
    for (const [i, t] of tiers.entries()) {
      const th = parseFloat(t.threshold);
      const rt = parseFloat(t.rate);
      if (!Number.isFinite(th) || th <= 0) return `Threshold ${i + 1} needs a revenue amount.`;
      if (!Number.isFinite(rt) || rt <= 0 || rt > 100) return `Threshold ${i + 1} needs a rate between 0 and 100%.`;
      if (th <= prevTh) return `Threshold ${i + 1} must be higher than the one above it.`;
      if (rt <= prevRate) return `Threshold ${i + 1}'s rate must be higher than ${prevRate}%.`;
      prevTh = th;
      prevRate = rt;
    }
    return null;
  }, [staffId, baseRate, picked, tiers, shares, services]);

  const save = async () => {
    if (problem) { toast.error(problem); return; }
    setSaving(true);
    try {
      const r = await staffApi.put(`/commission-tiers/${staffId}`, {
        service_ids: Array.from(picked),
        service_shares: Array.from(picked).reduce((acc, sid) => {
          acc[sid] = parseFloat(shares[sid] ?? "100");
          return acc;
        }, {}),
        base_rate: parseFloat(baseRate),
        tiers: tiers.map((t) => ({ threshold: parseFloat(t.threshold), rate: parseFloat(t.rate) })),
        active: true,
      });
      const re = r.data?.rerate || {};
      toast.success(
        re.rows > 0
          ? `Profile saved. ${re.rows} commission row${re.rows === 1 ? "" : "s"} re-rated at ${re.rate}% for this month.`
          : "Tiered commission profile saved.",
      );
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="tier-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Tiered Commission Profile" : "New Tiered Commission Profile"}</DialogTitle>
          <DialogDescription>
            Revenue from the ticked treatments is accumulated per calendar month on the
            treatment's <strong>base price</strong> (before service charge and PPN). Crossing a
            threshold re-rates the whole month at the higher percentage. Set a revenue share
            below 100% for a treatment performed by two people.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* 1 · Staff assignment */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">1 · Staff member</Label>
            <Select value={staffId} onValueChange={setStaffId} disabled={isEdit}>
              <SelectTrigger className="h-9" data-testid="tier-staff-select">
                <SelectValue placeholder="Select a staff member…" />
              </SelectTrigger>
              <SelectContent>
                {assignable.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`tier-staff-opt-${u.id}`}>
                    {u.full_name || u.email}{u.position ? ` · ${u.position}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              One profile per staff member — anyone already holding a profile is hidden here.
            </p>
          </div>

          {/* 2 · Treatments */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                2 · Treatments in this profile
              </Label>
              <span className="text-xs text-muted-foreground" data-testid="tier-picked-count">
                {picked.size} selected
              </span>
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search treatments…"
                className="h-9 pl-8"
                data-testid="tier-service-search"
              />
            </div>
            <div className="rounded-lg border border-border divide-y max-h-72 overflow-y-auto">
              {orderedCats.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6" data-testid="tier-no-services">
                  No treatments match that search.
                </p>
              )}
              {orderedCats.map((cat) => {
                const list = svcByCat.get(cat.id) || [];
                const expanded = open.has(cat.id) || Boolean(query.trim());
                const selectedHere = list.filter((s) => picked.has(s.id)).length;
                return (
                  <div key={cat.id}>
                    <div className="flex items-center gap-2 px-3 py-2 bg-muted/30">
                      <button
                        type="button"
                        className="flex items-center gap-2 text-left text-xs uppercase tracking-wider font-semibold flex-1"
                        onClick={() => toggleCat(cat.id)}
                        data-testid={`tier-cat-toggle-${cat.id}`}
                      >
                        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        <span>{cat.name}</span>
                        <span className="text-[10px] font-normal normal-case text-muted-foreground">
                          · {list.length} service{list.length === 1 ? "" : "s"}
                          {selectedHere > 0 ? ` · ${selectedHere} ticked` : ""}
                        </span>
                      </button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 text-[11px]"
                        onClick={() => setAllInCat(list, selectedHere !== list.length)}
                        data-testid={`tier-cat-all-${cat.id}`}
                      >
                        {selectedHere === list.length ? "Clear" : "Select all"}
                      </Button>
                    </div>
                    {expanded && (
                      <div className="py-1">
                        {list.map((s) => (
                          <label
                            key={s.id}
                            className="flex items-center gap-2.5 px-3 py-1.5 pl-9 hover:bg-muted/40 cursor-pointer"
                          >
                            <Checkbox
                              checked={picked.has(s.id)}
                              onCheckedChange={() => toggleSvc(s.id)}
                              data-testid={`tier-svc-check-${s.id}`}
                            />
                            <span className="text-sm flex-1">{s.name}</span>
                            {s.active === false && (
                              <span className="text-[10px] text-muted-foreground">(inactive)</span>
                            )}
                            {picked.has(s.id) && (
                              <span
                                className="flex items-center gap-1.5 shrink-0"
                                onClick={(e) => e.preventDefault()}
                              >
                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                  Revenue share
                                </span>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="1"
                                  value={shares[s.id] ?? "100"}
                                  onChange={(e) => setShare(s.id, e.target.value)}
                                  className="h-7 w-16 text-xs"
                                  data-testid={`tier-svc-share-${s.id}`}
                                />
                                <span className="text-xs text-muted-foreground">%</span>
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 3 · Tier ladder */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">3 · Tier structure</Label>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Base rate %</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  value={baseRate}
                  onChange={(e) => setBaseRate(e.target.value)}
                  className="h-9 w-28"
                  data-testid="tier-base-rate"
                />
              </div>
              <p className="text-[11px] text-muted-foreground pb-2.5">
                Applies from the first rupiah of the month until a threshold is crossed.
              </p>
            </div>

            <div className="space-y-2">
              {tiers.map((t, i) => (
                <div key={`tier-${i}`} className="flex items-end gap-2 flex-wrap" data-testid={`tier-row-${i + 1}`}>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Threshold {i + 1} · monthly revenue</Label>
                    <Input
                      type="number"
                      min="0"
                      value={t.threshold}
                      onChange={(e) => setTier(i, { threshold: e.target.value })}
                      className="h-9 w-48"
                      data-testid={`tier-threshold-${i + 1}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Rate %</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={t.rate}
                      onChange={(e) => setTier(i, { rate: e.target.value })}
                      className="h-9 w-24"
                      data-testid={`tier-rate-${i + 1}`}
                    />
                  </div>
                  <span className="text-[11px] text-muted-foreground pb-2.5">
                    {Number(t.threshold) > 0 ? `from ${fm(t.threshold)} → ${Number(t.rate) || 0}% on the whole month` : ""}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-9 text-red-600"
                    onClick={() => removeTier(i)}
                    data-testid={`tier-remove-${i + 1}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>

            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5 h-8"
              onClick={addTier}
              disabled={tiers.length >= MAX_TIERS}
              data-testid="tier-add-threshold"
            >
              <Plus className="w-3.5 h-3.5" />
              {tiers.length >= MAX_TIERS ? "Maximum 4 thresholds" : "Add threshold"}
            </Button>
          </div>

          {problem && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="tier-validation-msg">
              {problem}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="tier-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving || Boolean(problem)} data-testid="tier-save-btn">
            {saving ? "Saving…" : "Save Profile"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
